import React, { useState } from 'react';
import type { CategoryStatus, MultiSelectProps } from './interfaces';
import styles from './MultiSelect.module.scss';
import Icon from '../Icon/Icon';
import clsx from 'clsx';
import useOnClickOutside from '../../../hooks/useOnClickOutside/useOnClickOutside';
import MultiSelectItem from './MultiSelectItem/MultiSelectItem';
import MultiSelectCategoryItem from './MultiSelectCategoryItem/MultiSelectCategoryItem';

const MultiSelect = ({
  label,
  items,
  isBoxOnGradient = false,
  className,
  toggleSelected,
  isSelected,
  setSelected,
  numberOfSelected,
}: MultiSelectProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const listBoxRef = React.useRef<HTMLUListElement>(null);
  const listBoxId = label;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSelectionOpen, setIsSelectionOpen] = useState(false);
  const ariaActiveDescendant =
    activeIndex === -1 ? '' : `listbox${listBoxId}-${activeIndex}`;

  const onSelectionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsSelectionOpen((isSelectionOpen) => !isSelectionOpen);
  };

  const getCategoryItems = (category: string) => {
    return [...items]
      .filter((item) => item.category === category)
      .map(({ label }) => label);
  };

  const getInactiveCategoryItems = (category: string) => {
    const categoryItems = getCategoryItems(category);

    const inactiveCategoryItems = categoryItems
      ? categoryItems.filter((item) => !isSelected(item))
      : [];

    return inactiveCategoryItems;
  };

  const getCategoryStatus = (category: string): CategoryStatus => {
    const categoryItems = getCategoryItems(category);
    const inactiveCategoryItems = getInactiveCategoryItems(category) || [];

    const length = categoryItems?.length ?? 0;

    if (inactiveCategoryItems.length === 0) {
      return 'all';
    }
    if (inactiveCategoryItems.length === length) {
      return 'none';
    }
    return 'some';
  };

  const toggleCategory = (category: string) => {
    const categoryItems = getCategoryItems(category) || [];
    const inactiveCategoryItems = getInactiveCategoryItems(category) || [];
    const categoryStatus = getCategoryStatus(category);

    setSelected((selected) => {
      if (categoryStatus === 'all') {
        return [...selected].filter(
          (element) => !categoryItems.includes(element)
        );
      }
      return [...selected, ...inactiveCategoryItems];
    });
  };

  const selectionItems = items.map((item, index) => {
    if (item.type === 'category') {
      const categoryStatus = getCategoryStatus(item.label);

      return (
        <MultiSelectCategoryItem
          key={'category-' + item.label}
          listBoxId={`listbox-category${listBoxId}-${index}`}
          onClick={() => toggleCategory(item.label)}
          isFocus={index === activeIndex}
          label={item.label}
          status={categoryStatus}
        />
      );
    }

    return (
      <MultiSelectItem
        key={'item-' + item.label}
        listBoxId={`listbox-item${listBoxId}-${index}`}
        onClick={() => toggleSelected(item.label)}
        isFocus={index === activeIndex}
        item={item}
        isActive={isSelected(item.label)}
        indented={Boolean(item.category)}
      />
    );
  });

  useOnClickOutside(ref, () => setIsSelectionOpen(false));

  const handleKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        if (items[activeIndex].type === 'category') {
          toggleCategory(items[activeIndex].label);
        } else {
          toggleSelected(items[activeIndex].label);
        }
        event.preventDefault();
        break;
      case 'ArrowDown':
        setActiveIndex((activeIndex + 1) % items.length);
        event.preventDefault();
        break;
      case 'ArrowUp':
        setActiveIndex((activeIndex - 1 + items.length) % items.length);
        event.preventDefault();
        break;
      case 'Home':
        setActiveIndex(0);
        event.preventDefault();
        break;
      case 'End':
        setActiveIndex(items.length - 1);
        event.preventDefault();
        break;
      case 'Escape':
      case 'Tab':
        setIsSelectionOpen(false);
        if (buttonRef.current) {
          buttonRef.current.focus();
        }
        event.preventDefault();
        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    if (isSelectionOpen && listBoxRef.current) {
      listBoxRef.current.focus();
    }
  }, [isSelectionOpen]);

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (ref.current && ref.current.contains(event.target as Node)) {
      return;
    }
    setIsSelectionOpen(false);
  };

  return (
    <div
      className={clsx(styles.multiSelect, className)}
      ref={ref}
      onBlur={handleBlur}
    >
      <button
        ref={buttonRef}
        className={clsx(styles.button, {
          [styles.withGradient]: isBoxOnGradient,
          [styles.active]: isSelectionOpen,
        })}
        onClick={onSelectionClick}
        aria-haspopup="listbox"
        aria-expanded={isSelectionOpen}
      >
        <span
          className={clsx(styles.label, {
            [styles.withGradient]: isBoxOnGradient,
          })}
        >
          {label}
          <span
            className={clsx(styles.placeholderNumber, {
              [styles.isActive]: numberOfSelected,
              [styles.withGradient]: isBoxOnGradient,
            })}
          >
            {numberOfSelected}
          </span>
        </span>
        <span
          className={clsx(styles.icon, {
            [styles.withGradient]: isBoxOnGradient,
            [styles.rotated]: isSelectionOpen,
          })}
        >
          <Icon variant={'action/chevron-down'} size="iconSize24" />
        </span>
      </button>
      <ul
        className={clsx(styles.selectionList)}
        onKeyDown={handleKeyDown}
        id={`listbox${listBoxId}`}
        role="listbox"
        tabIndex={0}
        aria-label={label}
        aria-multiselectable={true}
        aria-activedescendant={ariaActiveDescendant}
        ref={listBoxRef}
      >
        {selectionItems}
      </ul>
    </div>
  );
};

export default MultiSelect;
